import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { brand, shadows } from '../theme';
import { gsap, useGsap, QUERIES, reveal } from '../animation/gsap';
import { useServices } from '../content/ContentProvider';
import ServiceIcon from '../components/ServiceIcon';

/**
 * The seven services, as a horizontal scroll.
 *
 * On a desktop the section pins and the row of cards travels sideways while the
 * page scrolls down, with a gold progress bar tracking how far through you are.
 *
 * On a phone this is a plain vertical grid. Hijacking the scroll direction on a
 * touch screen is genuinely unpleasant to use — it fights the gesture people
 * are already making — so the effect is simply not applied there. Same markup,
 * same content, no pin.
 */
export default function ServicesHorizontal() {
	const services = useServices();

	const root = useGsap(
		({ mm }) => {
			const q = gsap.utils.selector(root);

			mm.add(QUERIES.desktop, () => {
				const track = q('[data-track]')[0];
				const progress = q('[data-progress]')[0];

				if (!track) {
					return undefined;
				}

				/*
				 * Switch the section into its cinematic layout.
				 *
				 * The stylesheet lays this section out as an ordinary grid, and the
				 * sideways row only exists while this attribute is set. That order
				 * matters: the row is far wider than the screen and the section
				 * clips its overflow, so if the row were the default and this code
				 * did not run — reduced motion, or the bundle failing to load —
				 * cards four to seven would be off the edge with no way to reach
				 * them. Laying it out that way only once the animation that moves
				 * it definitely exists means that cannot happen.
				 */
				root.current.dataset.cinematic = 'true';

				// How far the row has to travel for its last card to come to rest at
				// the right-hand edge. Measured in a function so it is recalculated
				// when the window is resized, rather than baked in at page load.
				const distance = () => Math.max(0, track.scrollWidth - root.current.offsetWidth + 96);

				gsap.to(track, {
					x: () => -distance(),
					ease: 'none',
					scrollTrigger: {
						trigger: root.current,
						start: 'top top',
						end: () => `+=${distance()}`,
						scrub: 1,
						pin: true,
						anticipatePin: 1,
						// Without this the distance above is measured once and the
						// section breaks after a resize.
						invalidateOnRefresh: true,
						onUpdate: (self) => {
							if (progress) {
								gsap.set(progress, { scaleX: self.progress });
							}
						},
					},
				});

				// The cards lift slightly as they enter from the right.
				//
				// The lift ends at 'left 82%', not 'left center': the LAST card
				// comes to rest near the right edge and its left never reaches the
				// centre, so a centre endpoint left it frozen mid-lift, sitting
				// 11px below its neighbours for good. Every card's left does cross
				// 82% on any sane desktop width, so every lift completes.
				q('[data-card]').forEach((card) => {
					gsap.fromTo(
						card,
						{ y: 40 },
						{
							y: 0,
							ease: 'none',
							scrollTrigger: {
								trigger: card,
								containerAnimation: gsap.getTweensOf(track)[0],
								start: 'left right',
								end: 'left 82%',
								scrub: true,
							},
						}
					);
				});

				// Put the plain grid back if the visitor switches reduced motion on,
				// or when the section unmounts on a route change.
				return () => {
					if (root.current) {
						delete root.current.dataset.cinematic;
					}
				};
			});

			// Everywhere else, the cards just fade up as they arrive.
			mm.add(QUERIES.mobile, () => {
				reveal(q('[data-card]'), {
					trigger: root.current,
					start: 'top 75%',
					stagger: 0.08,
				});
			});
		},
		[services.length]
	);

	return (
		<Box
			ref={root}
			component="section"
			id="services"
			sx={{
				position: 'relative',
				backgroundColor: brand.surface,
				py: 8,

				/*
				 * The cinematic layout, applied by GSAP once the animation that
				 * drives it exists. Everything above this is the plain, always-safe
				 * version of the section.
				 */
				'&[data-cinematic="true"]': {
					overflow: 'hidden',
					py: 0,
					minHeight: '100vh',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
				},
			}}
		>
			<Container sx={{ mb: { xs: 5, lg: 6 } }}>
				<Typography variant="overline" sx={{ display: 'block', color: brand.accent, mb: 1.5 }}>
					What we do
				</Typography>
				<Typography variant="h2" component="h2" sx={{ maxWidth: '20ch' }}>
					Seven trades, one crew
				</Typography>
				<Typography sx={{ mt: 2.5, maxWidth: '54ch', color: brand.textMuted, fontSize: '1.05rem' }}>
					Most jobs need more than one skill. We cover the lot, so you are not chasing four
					different trades to finish one room.
				</Typography>
			</Container>

			{/*
				The cards. A plain responsive grid by default — which is what a
				phone gets, and what anyone gets if the animation does not run. The
				sideways row is switched on by the parent's data-cinematic attribute.
			*/}
			<Box
				data-track
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
					gap: 3,
					px: 3,
					maxWidth: '75rem',
					marginInline: 'auto',

					'[data-cinematic="true"] &': {
						display: 'flex',
						width: 'max-content',
						maxWidth: 'none',
						marginInline: 0,
						willChange: 'transform',
					},
				}}
			>
				{services.map((service, index) => (
					<Card
						key={service.slug}
						data-card
						sx={{
							// Fixed width only in the travelling row; in the grid the
							// column decides how wide the card is.
							'[data-cinematic="true"] &': { flex: '0 0 22rem', width: '22rem' },
							p: 4,
							display: 'flex',
							flexDirection: 'column',
							backgroundColor: brand.white,
							transition: 'box-shadow 300ms ease, border-color 300ms ease',
							'&:hover': { boxShadow: shadows.lift, borderColor: brand.accent },
						}}
					>
						<Box
							sx={{
								width: '3.5rem',
								height: '3.5rem',
								borderRadius: 2,
								display: 'grid',
								placeItems: 'center',
								backgroundColor: 'rgb(182 142 75 / 12%)',
								color: brand.accent,
								mb: 3,
							}}
						>
							<ServiceIcon svg={service.icon_svg} size={28} />
						</Box>

						<Typography
							variant="overline"
							sx={{ color: brand.grey, fontSize: '0.7rem', mb: 1 }}
						>
							{String(index + 1).padStart(2, '0')}
						</Typography>

						<Typography variant="h4" component="h3" sx={{ mb: 1.5 }}>
							{service.title}
						</Typography>

						<Typography sx={{ color: brand.textMuted, flex: 1 }}>{service.summary}</Typography>

						<Button
							component={RouterLink}
							to={`/services#${service.slug}`}
							sx={{ mt: 3, alignSelf: 'flex-start', px: 0, color: brand.accent, fontWeight: 700 }}
						>
							Read more →
						</Button>
					</Card>
				))}
			</Box>

			{/* How far through the row you are. Only shown when the row actually
			    travels, otherwise it would sit there permanently empty. */}
			<Container sx={{ display: 'none', mt: 6, '[data-cinematic="true"] &': { display: 'block' } }}>
				<Box sx={{ height: '3px', backgroundColor: brand.greyLight, borderRadius: '2px' }}>
					<Box
						data-progress
						sx={{
							height: '100%',
							backgroundColor: brand.accent,
							borderRadius: '2px',
							transform: 'scaleX(0)',
							transformOrigin: 'left',
						}}
					/>
				</Box>
			</Container>
		</Box>
	);
}
